import type React from "react";
import { useEffect, useState } from "react";
import { AccountIcon, LockIcon } from "~/components/icons";
import type { FlowContext } from "~/components/KratosFlow/Flow";
import {
	translateErrorMessage,
	translateInputText,
} from "~/components/KratosFlow/translate";
import type { NodeInputProps } from "./helpers";

const InputIcon: React.FC<{ name: string }> = ({ name }) => {
	switch (name) {
		case "identifier":
			return (
				<AccountIcon
					color={{
						dark: "#C6C6C5",
						light: "#C6C6C5",
					}}
				/>
			);
		case "email":
			return (
				<AccountIcon
					color={{
						dark: "#C6C6C5",
						light: "#C6C6C5",
					}}
				/>
			);
		case "password":
			return (
				<LockIcon
					color={{
						dark: "#C6C6C5",
						light: "#C6C6C5",
					}}
				/>
			);
		default:
			return <></>;
	}
};
interface Props extends NodeInputProps {
	context: FlowContext;
	// biome-ignore lint/suspicious/noExplicitAny: any is required for the validate function
	validate?: (value: any) => boolean;
}

export const NodeInputDefault: React.FC<Props> = ({
	node,
	attributes,
	value = "",
	setValue,
	disabled,
	context,
	validate,
}) => {
	const [hasError, setHasError] = useState(false);
	// Some attributes have dynamic JavaScript - this is for example required for WebAuthn.
	const onClick = () => {
		// This section is only used for WebAuthn. The script is loaded via a <script> node
		// and the functions are available on the global window level. Unfortunately, there
		// is currently no better way than executing eval / function here at this moment.
		if (attributes.onclick) {
			const run = new Function(attributes.onclick);
			run();
		}
	};

	useEffect(() => {
		setHasError(node.messages.some(({ type }) => type === "error"));
	}, [node.messages]);

	// Render a generic text input field.
	return (
		<div className="form-control mt-4">
			<fieldset
				className={`t4-form-fieldset ${hasError && "error"} ${
					disabled && "disabled"
				}`}
			>
				<legend className="t4-input-label text-xs">
					{translateInputText(node).toUpperCase()}
				</legend>
				<div className="flex justify-center items-center px-3">
					{context.withIcon && <InputIcon name={attributes.name} />}
					<input
						className="input w-full"
						onClick={onClick}
						onBlur={() => {
							if (validate) {
								setHasError(!validate(value));
							}
						}}
						onChange={(e) => {
							// unset error when recover from error
							if (validate?.(e.target.value)) {
								setHasError(false);
							}
							setValue(e.target.value).then();
						}}
						id={attributes.name}
						type={attributes.type}
						name={attributes.name}
						value={value}
						disabled={attributes.disabled || disabled}
						placeholder={`Enter your ${translateInputText(node).toLowerCase()}`}
						autoComplete="off"
					/>
				</div>
			</fieldset>
			{node.messages.length > 0 && (
				<label className="label" htmlFor={undefined}>
					{/* crypto.randomUUID() replaces nanoid(4); stable index keys are disallowed by biome noArrayIndexKey */}
					{node.messages.map(({ text, id }) => (
						<span
							className={`t4-label-text-alt ${hasError ? "error-text" : ""}`}
							key={`${id}-${crypto.randomUUID()}`}
							data-testid={`ui/message/${id}`}
						>
							{translateErrorMessage(text)}
						</span>
					))}
				</label>
			)}
		</div>
	);
};
